export interface RetryPolicy {
  /** total attempts per fetcher, including the first */
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
}

export const DEFAULT_RETRY: RetryPolicy = { maxAttempts: 4, baseDelayMs: 500, maxDelayMs: 60_000 };

/** Delay before retry number `attempt` (1-based); honours Retry-After when given. */
export function backoffDelay(attempt: number, retryAfterMs: number | undefined, policy: RetryPolicy, rand = Math.random): number {
  const exp = policy.baseDelayMs * 2 ** (attempt - 1);
  const jitter = exp * 0.25 * rand();
  const base = retryAfterMs !== undefined ? Math.max(retryAfterMs, 0) + jitter : exp + jitter;
  return Math.min(base, policy.maxDelayMs);
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException('aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Limits how many requests run at once per key (e.g. per log), so prefetching does not trip rate limits. */
export class Semaphore {
  private active = 0;
  private queue: Array<() => void> = [];
  constructor(readonly limit: number) {}

  async acquire(): Promise<() => void> {
    if (this.active < this.limit) {
      this.active++;
    } else {
      await new Promise<void>((resolve) => this.queue.push(resolve));
      this.active++;
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.active--;
      const next = this.queue.shift();
      if (next) next();
    };
  }
}
